import { useEffect, useMemo, useState } from "react";

import { create } from "@bufbuild/protobuf";
import { LinkSimpleIcon } from "@phosphor-icons/react";

import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import GridWrapper, { GridAlignContent, GridAlignItems } from "@gx/dls/containers/GridWrapper";
import { SourceSyncStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { ListSourceSyncsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";
import { SourceSyncOperationMetadataBrowserbase } from "@gx/ts-contracts/services/source/resources/source_sync_operation_metadata/source_sync_operation_metadata_pb";

import EmptyLayout from "@/layouts/EmptyLayout";

import EditSourceContentToolbar from "@/pages/sources/components/edit/tabs/content/EditSourceContentToolbar";
import { shouldRefetchSourceSyncs } from "@/pages/sources/components/edit/utils";
import SourceContentFileBadge from "@/pages/sources/components/SourceContentFileBadge";

import { useListSourceSyncsQuery } from "@/api/queries/syncs";

interface EditSourceContentBrowserbaseProps {
  source: SourceResource;
  items: SourceSyncOperationMetadataBrowserbase[];
  search: string;
  onSearchChange: (value: string) => void;
}

interface EditSourceContentBrowserbaseState {
  highRes: boolean;
}

const DEFAULT_STATE: EditSourceContentBrowserbaseState = {
  highRes: false,
};

const EditSourceContentBrowserbase = ({
  source,
  items,
  search,
  onSearchChange,
}: EditSourceContentBrowserbaseProps) => {
  const [state, setState] = useState<EditSourceContentBrowserbaseState>(DEFAULT_STATE);

  const { data: syncs } = useListSourceSyncsQuery({
    input: create(ListSourceSyncsRequestSchema, { sourceId: source.id }),
    options: {
      refetchInterval: (query) => shouldRefetchSourceSyncs(5000, query.state.data?.syncs),
    },
  });

  useEffect(() => {
    if (!syncs || syncs.syncs.length === 0) {
      return;
    }
    const allFinished = syncs.syncs.every(
      (s) =>
        s.syncStatus !== SourceSyncStatus.RUNNING &&
        s.syncStatus !== SourceSyncStatus.START &&
        s.syncStatus !== SourceSyncStatus.UNSPECIFIED,
    );
    if (!allFinished) {
      setState((prev) => ({ ...prev, highRes: true }));
      return;
    }
    const timeoutId = setTimeout(() => setState((prev) => ({ ...prev, highRes: true })), 5000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [syncs]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.title?.toLowerCase().includes(query) || item.url?.toLowerCase().includes(query),
    );
  }, [items, search]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} fillWidth fillHeight minHeight={0}>
      <EditSourceContentToolbar
        search={search}
        count={items.length}
        onSearchChange={onSearchChange}
      />
      <FlexWrapper padding="12px" fillWidth fillHeight minHeight={0} overflow="auto">
        {filtered.length > 0 ? (
          <GridWrapper
            columns="repeat(auto-fill, minmax(200px, 1fr))"
            gap={8}
            alignItems={GridAlignItems.START}
            alignContent={GridAlignContent.START}
            fillWidth
          >
            {filtered.map((item) => {
              const imgUrl = state.highRes ? item.screenshotUri : item.screenshotLowResUri;
              return (
                <SourceContentFileBadge
                  key={item.url}
                  header={item.title ?? "Untitled"}
                  subHeader={item.url}
                  imgUrl={imgUrl ?? null}
                  icon={LinkSimpleIcon}
                />
              );
            })}
          </GridWrapper>
        ) : (
          <EmptyLayout title="No content" description="No content matches your search" />
        )}
      </FlexWrapper>
    </FlexWrapper>
  );
};

export default EditSourceContentBrowserbase;
